import styled, { keyframes } from 'styled-components';

import { Text } from '../../../../cdl/Text/Text';
import { Characters } from '../../../../common/helpers/unicode.helper';
import { IconCircleCheck } from '../../../../common/icons/cdl/CircleCheck';
import { Spinner } from '../../../../common/Spinner/Spinner';
import { TextShimmer } from '../../../../common/TextShimmer/TextShimmer';
import { Box } from '../../../../common/ui/Box';
import { theme } from '../../../../common/ui/theme';

const fadeIn = keyframes`
    0% {
        opacity: 0;
    }
    100% {
        opacity: 1;
    }
`;

const moveIn = keyframes`
    0% {
        transform: translateY(-50%);
    }
    100% {
        transform: translateY(0);
    }
`;

const AnimatedStepContent = styled(Box)`
    animation:
        ${fadeIn} 200ms ease-out,
        ${moveIn} 200ms ease-out;
`;

interface ProgressStepProps {
    text: string;
    isActive: boolean;
    description?: string;
}

const StepIcon = ({ isActive }: { isActive: boolean }) => {
    return isActive ? (
        <Box display="flex" justifyContent="center" alignItems="center">
            <Spinner size={18} color={theme.colors.special.emphasis} />
        </Box>
    ) : (
        <Box color="foreground.subtle">
            <IconCircleCheck width={20} height={20} />
        </Box>
    );
};

const StepText = ({ text, isActive }: { text: string; isActive: boolean }) => {
    return isActive ? (
        <TextShimmer variant="body" fontWeight={600} color="special.emphasis">
            {text}
            {Characters.HORIZONTAL_ELLIPSIS}
        </TextShimmer>
    ) : (
        <Text variant="body" fontWeight={600} color="foreground.subtle">
            {text}
        </Text>
    );
};

const ProgressStep = ({ text, isActive, description }: ProgressStepProps) => {
    return (
        <AnimatedStepContent
            display="grid"
            gridTemplateColumns="20px auto"
            gridTemplateRows={description ? '20px auto' : undefined}
            alignItems="center"
            gap={8}
            width="80%"
        >
            <StepIcon isActive={isActive} />
            <StepText text={text} isActive={isActive} />

            {description ? (
                <>
                    <Box width="2px" backgroundColor="border.default" marginLeft="9px" height="90%" />
                    <Text color="foreground.subtle">{description}</Text>
                </>
            ) : null}
        </AnimatedStepContent>
    );
};

interface IntelligenceProgressIndicatorProps {
    steps: Array<{
        text: string;
        description?: string;
        isActive: boolean;
        isVisible: boolean;
    }>;
}

export const IntelligenceProgressIndicator = ({ steps }: IntelligenceProgressIndicatorProps) => {
    return (
        <Box display="grid" rowGap={16}>
            {steps.map((step, index) =>
                step.isVisible ? (
                    <ProgressStep
                        key={index}
                        text={step.text}
                        isActive={step.isActive}
                        description={step.description}
                    />
                ) : null
            )}
        </Box>
    );
};
